import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorMessage } from '@hookform/error-message'
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react'
import { Link, useNavigate } from 'react-router-dom'

import PhoneInput from 'react-phone-number-input/react-hook-form-input-core'
import type { MetadataJson } from 'libphonenumber-js'

import phoneMeta from '@/cart/utils/metadata.phone.json'

import { useAppContext } from '@/main/app/context'
import { useAppDispatch, useAppSelector } from '@/main/app/hooks/storeHooks'
import { ProfileAndBillingSchema, ProfileAndBilling } from '@/main/app/schema'

import { Button } from '@/main/components/Button/Button'
import { defaultProfileSaved, listFormSaved } from '@/main/features/List/listSlice'

import { useListOutletContext } from '@/cart/hooks/useListOutletContext'
import { Switch } from '@headlessui/react'
import {
  fieldsetClassList,
  inputClassList,
  labelClassList,
  legendClassList,
} from '@/main/app/utils/classNames'
import { formatPhoneNumber, isMaybePhoneNumber } from '@/cart/utils/formatPhoneNumber'

export function ProfileAndBillingForm() {
  const {
    formPaths: [, step2, step3],
  } = useAppContext()

  const { id, form } = useListOutletContext()

  const defaultProfile = useAppSelector((state) => state.lists.defaultProfile)

  const [isDefaultProfile, setIsDefaultProfile] = useState(
    !form?.profile?.values && defaultProfile !== undefined
  )

  const defaultValues = {
    profile: form?.profile?.values
      ? {
          firstName: form.profile.values.firstName,
          lastName: form.profile.values.lastName,
          email: form.profile.values.email,
          phone: form.profile.values.phone,
        }
      : defaultProfile
      ? {
          firstName: defaultProfile.firstName,
          lastName: defaultProfile.lastName,
          email: defaultProfile.email,
          phone: !isMaybePhoneNumber(defaultProfile.phone)
            ? formatPhoneNumber(defaultProfile.phone, true)
            : defaultProfile.phone,
        }
      : undefined,
    billing: form?.billing?.values,
  }

  const isStatusSaved =
    form?.profile?.status === 'saved' &&
    form.billing?.status === 'saved' &&
    form.dates?.status === 'saved' &&
    form.shipping?.status === 'saved'

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ProfileAndBilling>({
    defaultValues,
    resolver: zodResolver(ProfileAndBillingSchema),
  })

  const onSubmit = (data: ProfileAndBilling, e?: React.BaseSyntheticEvent) => {
    const nativeEvent = e?.nativeEvent as SubmitEvent

    dispatch(listFormSaved(id, data))

    if (isDefaultProfile) {
      dispatch(defaultProfileSaved(data.profile))
    }

    if (nativeEvent.submitter?.id === 'skip-to-last') {
      navigate(`/${id}/form/${step3}`)
    } else {
      navigate(`/${id}/form/${step2}`)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h2 className="twc-font-medium">1. Contact &amp; Project</h2>

      <p className="twc-prose-sm twc-text-rose-700 twc-mb-6 dark:twc-text-rose-300">
        All fields are required.
      </p>

      <h3>Contact Info</h3>

      <fieldset role="group" className={`${fieldsetClassList} twc-mb-8 twc-gap-6`}>
        <legend className={legendClassList}>Enter your name, email, and phone number</legend>

        <div className="twc-flex twc-flex-col sm:twc-flex-row twc-gap-4">
          <div className="twc-grow">
            <label className={labelClassList} htmlFor="profile.firstName">
              First Name
            </label>
            <input
              className={`${inputClassList} twc-mt-1`}
              type="text"
              id="profile.firstName"
              autoComplete="given-name"
              aria-invalid={errors.profile?.firstName ? 'true' : 'false'}
              aria-describedby="profile.firstName-feedback"
              {...register('profile.firstName', { required: true })}
            />
            <div id="profile.firstName-feedback" aria-live="polite">
              <ErrorMessage
                className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-600"
                errors={errors}
                name="profile.firstName"
                as="p"
              />
            </div>
          </div>

          <div className="twc-grow">
            <label className={labelClassList} htmlFor="profile.lastName">
              Last Name
            </label>
            <input
              className={`${inputClassList} twc-mt-1`}
              type="text"
              id="profile.lastName"
              autoComplete="family-name"
              aria-invalid={errors.profile?.lastName ? 'true' : 'false'}
              aria-describedby="profile.lastName-feedback"
              {...register('profile.lastName', { required: true })}
            />
            <div id="" aria-live="polite">
              <ErrorMessage
                className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-600"
                errors={errors}
                name="profile.lastName"
                as="p"
              />
            </div>
          </div>
        </div>

        <div>
          <label className={labelClassList} htmlFor="profile.email">
            Email
          </label>

          <input
            className={`${inputClassList} twc-mt-1`}
            type="email"
            id="profile.email"
            autoComplete="email"
            aria-invalid={errors.profile?.email ? 'true' : 'false'}
            aria-describedby="profile.email-feedback"
            {...register('profile.email', { required: true })}
          />

          <div id="" aria-live="polite">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-600"
              errors={errors}
              name="profile.email"
              as="p"
            />
          </div>
        </div>

        <div>
          <label className={labelClassList} htmlFor="profile.phone">
            Phone
          </label>

          <PhoneInput
            name="profile.phone"
            control={control}
            defaultValue={defaultValues?.profile?.phone}
            rules={{ required: true }}
            className={`${inputClassList} twc-mt-1`}
            country="US"
            autoComplete="phone"
            id="profile.phone"
            aria-invalid={errors.profile?.phone ? 'true' : 'false'}
            aria-describedby="profile.phone-feedback"
            metadata={phoneMeta as MetadataJson}
          />

          {/* <Controller
            name="profile.phone"
            control={control}
            defaultValue={defaultValues?.profile?.phone}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <PhoneInput
                className={`${inputClassList} twc-mt-1`}
                country="US"
                id="profile.phone"
                {...field}
              />
            )}
          /> */}

          {/* <input
            className={`${inputClassList} twc-mt-1`}
            type="tel"
            autoComplete="tel"
            id="profile.phone"
            aria-invalid={errors.profile?.phone ? 'true' : 'false'}
            aria-describedby="profile.phone-feedback"
            {...register('profile.phone', { required: true })}
          /> */}

          <div id="" aria-live="polite">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-600"
              errors={errors}
              name="profile.phone"
              as="p"
            />
          </div>
        </div>

        <Switch.Group>
          <div className="twc-flex twc-items-center twc-mt-4">
            <Switch
              checked={isDefaultProfile}
              onChange={setIsDefaultProfile}
              className={`${
                isDefaultProfile
                  ? '!twc-bg-emerald-600 dark:!twc-bg-emerald-600'
                  : '!twc-bg-gray-200 dark:!twc-bg-gray-600'
              } twc-relative twc-inline-flex twc-h-6 twc-w-11 twc-items-center twc-rounded-full twc-transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  isDefaultProfile ? 'twc-translate-x-6' : 'twc-translate-x-1'
                } twc-inline-block twc-h-4 twc-w-4 twc-transform twc-rounded-full twc-bg-white twc-transition-transform`}
              />
            </Switch>
            <Switch.Label className="twc-ml-2">Set as default contact info</Switch.Label>
          </div>
        </Switch.Group>
      </fieldset>

      <h3>Project Info</h3>
      <fieldset role="group" className={`${fieldsetClassList} twc-mb-8 twc-gap-6`}>
        <legend className={legendClassList}>
          Add your project name and billing party info. We will add it to your quote and invoice.
        </legend>

        <div>
          <label className={labelClassList} htmlFor="billing.reference">
            Project Name/Reference/Set Name
          </label>

          <input
            className={`${inputClassList} twc-my-2`}
            type="text"
            id="billing.reference"
            aria-invalid={errors.billing?.reference ? 'true' : 'false'}
            aria-describedby="ref-desc"
            {...register('billing.reference', { required: true })}
          />

          <p
            id="ref-desc"
            className="twc-my-0 twc-text-zinc-500/80 dark:twc-text-zinc-300/80 twc-prose-sm"
          >
            e.g. <em>ACME TV: Shawn&apos;s Bedroom</em>
          </p>

          <div id="billing.reference-feedback" aria-live="polite">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-600"
              errors={errors}
              name="billing.reference"
              as="p"
            />
          </div>
        </div>

        <div>
          <label className={labelClassList} htmlFor="billing.client">
            Billing Party Name
          </label>

          <input
            className={`${inputClassList} twc-my-2`}
            type="text"
            id="billing.client"
            autoComplete="organization"
            aria-invalid={errors.billing?.client ? 'true' : 'false'}
            aria-describedby="client-desc"
            {...register('billing.client', { required: true })}
          />

          <p
            id="client-desc"
            className="twc-my-0 twc-text-zinc-500/80 dark:twc-text-zinc-300/80 twc-prose-sm"
          >
            Client or Company to Bill To - No payment is needed at this time.
            <br />
            Please write name how it should appear on a quote/invoice. Please refrain from writing a
            billing address.
          </p>

          <div id="billing.client-feedback" aria-live="polite">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-600"
              errors={errors}
              name="billing.client"
              as="p"
            />
          </div>
        </div>

        <div>
          <label className={labelClassList} htmlFor="billing.cc">
            Additional Email (CC) <small>(optional)</small>
          </label>

          <input
            className={`${inputClassList} twc-my-2`}
            type="email"
            id="billing.cc"
            autoComplete="off"
            aria-invalid={errors.billing?.cc ? 'true' : 'false'}
            aria-describedby="cc-desc"
            {...register('billing.cc')}
          />

          <p
            id="cc-desc"
            className="twc-my-0 twc-text-zinc-500/80 dark:twc-text-zinc-300/80 twc-prose-sm"
          >
            Add an additional e-mail to CC the rental info and quote.
          </p>

          <div id="billing.cc-feedback" aria-live="polite">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-prose-sm twc-text-red-600"
              errors={errors}
              name="billing.cc"
              as="p"
            />
          </div>
        </div>
      </fieldset>

      <div className="twc-flex twc-flex-col twc-items-end twc-gap-8">
        <div className="twc-flex twc-flex-wrap twc-gap-4 twc-mb-6">
          <Button type="submit" variant="secondary">
            Save/Continue to Dates &amp; Shipping <ArrowRight size={18} />
          </Button>

          {isStatusSaved && (
            <Button type="submit" id="skip-to-last">
              Save &amp; Review Changes <ArrowRight size={18} />
            </Button>
          )}
        </div>

        <Link
          to={`/${id}`}
          className="twc-prose-sm twc-font-normal twc-underline twc-flex twc-gap-1 twc-items-center hover:twc-text-slate-600 hover:twc-no-underline"
        >
          <ArrowLeft /> Back to Summary
        </Link>
      </div>
    </form>
  )
}
